import { OpenPanel, type SessionReplayOptions } from '@openpanel/web';

import { resolveNoddiDualTldUrl } from '../resolveNoddiDualTldUrl';
import {
  consoleOpenPanelClient,
  OpenPanelClientShipper,
  silentOpenPanelClient,
  type OpenPanelClient
} from './openpanelClient';
import type { EventShipper } from './types';

export interface OpenPanelOptions {
  // Per-project client id from the OpenPanel dashboard (Settings → Clients).
  // Non-secret — safe to embed in a browser bundle. Required unless `stub`.
  clientId?: string;
  // First-party ingestion host of the self-hosted instance, e.g.
  // `https://analytics.noddi.co/api`. Dual-TLD hosts (`analytics.noddi.co|no`)
  // are rewritten at construct time via `resolveNoddiDualTldUrl` so the same
  // customer build works on app.noddi.co and app.noddi.no. See the per-site
  // alias table in noddi-infrastructure/services/open_panel/README.md.
  // Required for a real (non-stub) shipper; without it we stay not-ready and
  // never contact the SDK default (api.openpanel.dev).
  apiUrl?: string;
  // SDK-native auto-tracking. Off by default — callers emit sanitized
  // `tracking.screenView` themselves so raw `window.location.href` never ships.
  trackScreenViews?: boolean;
  trackOutgoingLinks?: boolean;
  // Auto-track clicks on elements annotated with `data-track`/`data-*` attributes.
  trackAttributes?: boolean;
  // Also track client-side hash (#) changes for screen views.
  trackHashChanges?: boolean;
  // rrweb-based session replay (OpenPanel v2+). Off unless `enabled: true`.
  // Recordings POST to the same `apiUrl`/`clientId` as events. Text and inputs
  // are masked by default; Noddi turns both off (`maskAllText`/`maskAllInputs`)
  // so recordings are readable — use `blockSelector` / `ignoreSelector` for
  // sensitive nodes. Use `sampleRate` (0..1) to cap recorded volume/cost.
  // See noddi-infrastructure/services/open_panel/README.md.
  sessionReplay?: SessionReplayOptions;
  // Project page of the OpenPanel dashboard, `https://op.zendos.io/navio/navio-customer`.
  // Lets `getReplay()` return a link to this session's recording.
  projectUrl?: string;
  // A predicate called before every event. Return false to drop the event.
  filter?: (payload: unknown) => boolean;
  // When true, the SDK will not send events until .ready() is called.
  disabled?: boolean;
  debug?: boolean;
  // When true, never talks to OpenPanel — logs calls to the console instead.
  // Use on local dev so you can see events without polluting the project.
  stub?: boolean;
}

// Adapt `@openpanel/web` once at the boundary so shippers stay on OpenPanelClient.
function adaptWebOpenPanel(sdk: OpenPanel): OpenPanelClient {
  const client: OpenPanelClient = {
    track: (name, props) => sdk.track(name, props),
    identify: (payload) => sdk.identify(payload),
    screenView: (path, props) => sdk.screenView(path, props),
    setGlobalProperties: (props) => sdk.setGlobalProperties(props),
    clear: () => sdk.clear(),
    revenue: (amount, props) => sdk.revenue(amount, props),
    upsertGroup: (payload) => sdk.upsertGroup(payload),
    setGroups: (groupIds) => sdk.setGroups(groupIds),
    getDeviceId: () => sdk.getDeviceId(),
    getSessionId: () => sdk.getSessionId(),
    get groups() {
      return sdk.groups;
    },
    set groups(value: string[] | undefined) {
      sdk.groups = value ?? [];
    }
  };
  return client;
}

const createOpenPanelClient = (options: OpenPanelOptions): { client: OpenPanelClient; ready: boolean } => {
  if (options.stub) {
    return { client: { ...consoleOpenPanelClient, groups: [] }, ready: true };
  }

  const apiUrl = resolveNoddiDualTldUrl(options.apiUrl);
  if (!options.clientId || !apiUrl) {
    return { client: silentOpenPanelClient, ready: false };
  }

  // Self-hosted OpenPanel 2.3.0+ accepts group / assign_group on /track.
  const sdk = new OpenPanel({
    clientId: options.clientId,
    apiUrl,
    trackScreenViews: options.trackScreenViews ?? false,
    trackOutgoingLinks: options.trackOutgoingLinks ?? false,
    trackAttributes: options.trackAttributes ?? false,
    trackHashChanges: options.trackHashChanges ?? false,
    sessionReplay: options.sessionReplay,
    filter: options.filter,
    disabled: options.disabled,
    debug: options.debug
  });
  return { client: adaptWebOpenPanel(sdk), ready: true };
};

export class OpenPanelShipper extends OpenPanelClientShipper implements EventShipper {
  constructor(options: OpenPanelOptions) {
    const { client, ready } = createOpenPanelClient(options);
    super(client, ready, options.projectUrl);
  }
}
